import React, { useState, useEffect, useRef } from 'react';
import {
  Fab,
  Popover,
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  styled,
  alpha,
  useTheme,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteIcon,
  Refresh as RefreshIcon,
  Mic as MicIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useWorkspace } from '../../utils/WorkspaceContext';
import api from '../../services/api';

const ChatContainer = styled(Paper)(({ theme }) => ({
  width: 420,
  height: 650,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    width: '90vw',
    height: '85vh',
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  boxShadow: theme.shadows[2],
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(2.5),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? theme.palette.background.default
    : theme.palette.grey[50],
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.primary, 0.2),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.text.primary, 0.3),
    },
  },
}));

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isUser',
})(({ theme, isUser }) => ({
  padding: theme.spacing(1.5, 2),
  maxWidth: '85%',
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  backgroundColor: isUser 
    ? theme.palette.primary.main 
    : theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.background.paper,
  color: isUser 
    ? theme.palette.primary.contrastText 
    : theme.palette.text.primary,
  borderRadius: isUser 
    ? theme.spacing(2.5, 2.5, 0.5, 2.5)
    : theme.spacing(2.5, 2.5, 2.5, 0.5),
  wordWrap: 'break-word',
  boxShadow: theme.palette.mode === 'dark'
    ? theme.shadows[4]
    : theme.shadows[1],
  border: isUser 
    ? 'none'
    : `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[3],
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-end',
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.8)
    : theme.palette.background.paper,
  backdropFilter: 'blur(10px)',
}));

const ThinkingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2),
  alignSelf: 'flex-start',
  color: theme.palette.text.secondary,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const TaskCreatedChip = styled(Chip)(({ theme }) => ({
  alignSelf: 'flex-start',
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.success.main, 0.2)
    : theme.palette.success.light,
  color: theme.palette.mode === 'dark'
    ? theme.palette.success.light
    : theme.palette.success.dark,
  border: `1px solid ${theme.palette.success.main}`,
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: 'inherit',
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(8),
  padding: theme.spacing(3),
  color: theme.palette.text.secondary,
}));

// The chat lives in sessionStorage so a page refresh keeps the conversation,
// while a new tab/window starts fresh (sessionStorage is per tab). One entry
// per workspace because the server-side context holds workspace-specific IDs.
const SESSION_KEY_PREFIX = 'chatbot:session:';

const loadSession = (workspaceId) => {
  if (!workspaceId) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_KEY_PREFIX + workspaceId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveSession = (workspaceId, session) => {
  if (!workspaceId) return;
  try {
    if (session) {
      sessionStorage.setItem(SESSION_KEY_PREFIX + workspaceId, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(SESSION_KEY_PREFIX + workspaceId);
    }
  } catch {
    // Quota or private-mode failures just mean the chat won't survive a refresh.
  }
};

const ChatBot = () => {
  const { currentWorkspace } = useWorkspace();
  const [anchorEl, setAnchorEl] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [thinking, setThinking] = useState(null);
  const [currentResponse, setCurrentResponse] = useState('');
  const [isSending, setIsSending] = useState(false);
  // Dictation: record with MediaRecorder, transcribe server-side with Whisper,
  // then drop the text into the input so the user can review it before sending.
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [dictationError, setDictationError] = useState(null);
  const mediaRecorderRef = useRef(null);
  // True once the current draft contains a transcript; sent as `dictated`.
  const draftDictatedRef = useRef(false);
  const messagesEndRef = useRef(null);
  const streamingAbortRef = useRef(null);
  // Server-built scratchpad of entities resolved in this chat (tracks, users,
  // tasks created, ...). Round-tripped on every request; reset on "Clear history".
  const chatContextRef = useRef(null);
  // Which workspace the current messages/context belong to, so a workspace
  // switch restores that workspace's session instead of mixing them.
  const sessionWorkspaceRef = useRef(null);

  // Restore (or swap) the session when the workspace changes.
  useEffect(() => {
    const workspaceId = currentWorkspace?.id;
    if (!workspaceId || sessionWorkspaceRef.current === workspaceId) return;
    if (streamingAbortRef.current) {
      streamingAbortRef.current.abort();
      streamingAbortRef.current = null;
    }
    const session = loadSession(workspaceId);
    sessionWorkspaceRef.current = workspaceId;
    chatContextRef.current = session?.context || null;
    setMessages(Array.isArray(session?.messages) ? session.messages : []);
    setCurrentResponse('');
    setThinking(null);
    setIsSending(false);
  }, [currentWorkspace?.id]);

  // Persist on every change. Only finished messages are stored; an in-flight
  // reply is dropped on refresh and the user can simply re-send.
  useEffect(() => {
    const workspaceId = sessionWorkspaceRef.current;
    if (!workspaceId) return;
    if (messages.length === 0 && !chatContextRef.current) {
      saveSession(workspaceId, null);
    } else {
      saveSession(workspaceId, { messages, context: chatContextRef.current });
    }
  }, [messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentResponse, thinking]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    stopRecording();
    if (streamingAbortRef.current) {
      streamingAbortRef.current.abort();
      streamingAbortRef.current = null;
    }
  };

  const open = Boolean(anchorEl);

  const sendMessage = async (retryMessage = null) => {
    // If called as an event handler (e.g., onClick={sendMessage}), React passes a MouseEvent.
    // Treat that as "no retry" instead of trying to serialize the event.
    if (retryMessage && typeof retryMessage === 'object' && typeof retryMessage.preventDefault === 'function') {
      retryMessage.preventDefault();
      retryMessage = null;
    }

    const isRetry = typeof retryMessage === 'string' && retryMessage.trim().length > 0;
    const messageToSend = (isRetry ? retryMessage : inputMessage).trim();
    if (!messageToSend || isSending || !currentWorkspace) return;

    const dictated = isRetry
      ? messages.some(msg => msg.role === 'user' && msg.content === messageToSend && msg.dictated)
      : draftDictatedRef.current;
    const userMessage = { role: 'user', content: messageToSend, ...(dictated ? { dictated: true } : {}) };
    if (!isRetry) {
      setMessages(prev => [...prev, userMessage]);
    }
    setInputMessage('');
    draftDictatedRef.current = false;
    setDictationError(null);
    setIsSending(true);
    setThinking(null);
    setCurrentResponse('');

    // Create conversation history for API (exclude error messages and ensure serializable).
    // Assistant turns keep the tool calls they made so the model can replay them
    // and refer back to IDs it already resolved ("modify the second one").
    const history = messages
      .filter(msg =>
        msg &&
        typeof msg === 'object' &&
        (msg.role === 'user' || msg.role === 'assistant') &&
        (msg.content || (msg.tool_calls && msg.tool_calls.length))
      )
      .map(msg => ({
        role: msg.role,
        content: msg.content || '',
        ...(msg.role === 'user' && msg.dictated ? { dictated: true } : {}),
        ...(msg.role === 'assistant' && msg.tool_calls?.length ? { tool_calls: msg.tool_calls } : {})
      }));

    try {
      const controller = new AbortController();
      streamingAbortRef.current = controller;
      const { signal } = controller;
      
      // Use fetch with api configuration for streaming
      const accessToken = localStorage.getItem('accessToken');
      const streamResponse = await fetch(`${api.defaults.baseURL}chat/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          message: userMessage.content,
          workspace_id: currentWorkspace.id,
          history,
          context: chatContextRef.current,
          dictated: Boolean(userMessage.dictated)
        }),
        signal
      });

      if (!streamResponse.ok) {
        throw new Error(`HTTP error! status: ${streamResponse.status}`);
      }

      const reader = streamResponse.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let sawDone = false;
      // Network chunks don't align with NDJSON lines, so carry the trailing
      // partial line over to the next read instead of dropping it.
      let pending = '';

      while (true) {
        if (signal.aborted) break;
        const { done, value } = await reader.read();
        const text = done
          ? pending + decoder.decode()
          : pending + decoder.decode(value, { stream: true });
        const lines = text.split('\n');
        pending = done ? '' : lines.pop();

        for (const line of lines) {
          if (signal.aborted) break;
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);

            switch (event.type) {
              case 'thinking':
                if (signal.aborted) break;
                setThinking(event.message);
                break;

              case 'token':
                if (signal.aborted) break;
                fullResponse += event.text;
                setCurrentResponse(fullResponse);
                break;

              case 'task_created':
                if (signal.aborted) break;
                setMessages(prev => [...prev, {
                  role: 'task_created',
                  task_id: event.task_id,
                  task_name: event.task_name
                }]);
                break;

              case 'done':
                if (signal.aborted) break;
                sawDone = true;
                if (event.context) {
                  chatContextRef.current = event.context;
                }
                if (event.full_response || event.tool_calls?.length) {
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: event.full_response || '',
                    tool_calls: event.tool_calls || []
                  }]);
                }
                setCurrentResponse('');
                setThinking(null);
                setIsSending(false);
                break;

              case 'error':
                if (signal.aborted) break;
                setMessages(prev => [...prev, {
                  role: 'error',
                  content: event.message || 'An error occurred',
                  originalMessage: userMessage.content
                }]);
                setThinking(null);
                setIsSending(false);
                break;

              default:
                break;
            }
          } catch (e) {
            console.error('Failed to parse event:', line, e);
          }
        }
        if (done) break;
      }

      // The stream ended without a terminal event (proxy cut it, server
      // crashed mid-reply): keep whatever text arrived and unlock the input.
      if (!sawDone && !signal.aborted) {
        if (fullResponse) {
          setMessages(prev => [...prev, { role: 'assistant', content: fullResponse, tool_calls: [] }]);
        }
        setCurrentResponse('');
        setThinking(null);
        setIsSending(false);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Swallow aborts: user intentionally cancelled (close/clear)
      } else {
        console.error('Chat error:', error);
        setMessages(prev => [...prev, {
          role: 'error',
          content: 'Failed to connect to chat service. Please try again.',
          originalMessage: userMessage.content
        }]);
      }
      setThinking(null);
      setIsSending(false);
    } finally {
      if (streamingAbortRef.current) {
        streamingAbortRef.current = null;
      }
    }
  };

  const transcribe = async (blob) => {
    setIsTranscribing(true);
    try {
      const extension = (blob.type.split('/')[1] || 'webm').split(';')[0];
      const formData = new FormData();
      formData.append('audio', blob, `dictation.${extension}`);
      const { data } = await api.post('chat/transcribe/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const text = (data?.text || '').trim();
      if (!text) {
        setDictationError("Didn't catch that. Try again.");
        return;
      }
      draftDictatedRef.current = true;
      setInputMessage(prev => (prev.trim() ? `${prev.trim()} ${text}` : text));
    } catch (error) {
      setDictationError(error.response?.data?.error || 'Transcription failed. Please try again.');
    } finally {
      setIsTranscribing(false);
    }
  };

  const startRecording = async () => {
    setDictationError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setDictationError('This browser does not support voice recording.');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setDictationError('Microphone access was denied.');
      return;
    }
    const recorder = new MediaRecorder(stream);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
      setIsRecording(false);
      // Closing the chat mid-recording discards the audio.
      if (recorder.discard || !chunks.length) return;
      transcribe(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }));
    };
    mediaRecorderRef.current = recorder;
    recorder.start();
    setIsRecording(true);
  };

  const stopRecording = (discard = true) => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.discard = discard;
      recorder.stop();
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording(false);
    } else {
      startRecording();
    }
  };

  // Release the microphone if the component unmounts mid-recording.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopRecording(), []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearHistory = () => {
    if (streamingAbortRef.current) {
      streamingAbortRef.current.abort();
      streamingAbortRef.current = null;
    }
    setThinking(null);
    setCurrentResponse('');
    setIsSending(false);
    setMessages([]);
    draftDictatedRef.current = false;
    chatContextRef.current = null;
    saveSession(sessionWorkspaceRef.current, null);
  };

  const retryMessage = (originalMessage, errorIndex) => {
    // Remove the error message and any subsequent messages
    setMessages(prev => prev.slice(0, errorIndex));
    // Resend the original message
    sendMessage(originalMessage);
  };

  const theme = useTheme();

  return (
    <>
      <Tooltip title="AI Assistant" placement="left" TransitionComponent={Fade}>
        <Fab
          color="primary"
          aria-label="chat"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            boxShadow: theme.shadows[8],
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: theme.shadows[12],
            },
          }}
        >
          <ChatIcon />
        </Fab>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        TransitionComponent={Fade}
        PaperProps={{
          sx: { 
            borderRadius: 3,
            boxShadow: theme.shadows[20],
          }
        }}
      >
        <ChatContainer elevation={0}>
          <ChatHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <BotIcon sx={{ fontSize: 28 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                  Powered by Gemini
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {messages.length > 0 && (
                <Tooltip title="Clear history">
                  <IconButton
                    size="small"
                    onClick={clearHistory}
                    sx={{ 
                      color: 'inherit',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.common.white, 0.2),
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  onClick={handleClose}
                  sx={{ 
                    color: 'inherit',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.2),
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </ChatHeader>

          <MessagesContainer>
            {messages.length === 0 && !currentResponse && (
              <EmptyStateBox>
                <BotIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  How can I help you today?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  I can help you create and manage tasks for your projects.
                </Typography>
              </EmptyStateBox>
            )}

            {messages.map((msg, idx) => {
              if (msg.role === 'user') {
                return (
                  <Fade in key={idx} timeout={300}>
                    <MessageBubble isUser elevation={0}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 18 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.9 }}>
                          You
                        </Typography>
                        {msg.dictated && (
                          <Tooltip title="Dictated">
                            <MicIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {msg.content}
                      </Typography>
                    </MessageBubble>
                  </Fade>
                );
              } else if (msg.role === 'assistant') {
                // Tool-only turns (no visible text) are kept for history but not rendered.
                if (!msg.content) return null;
                return (
                  <Fade in key={idx} timeout={300}>
                    <MessageBubble isUser={false} elevation={0}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <BotIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Assistant
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {msg.content}
                      </Typography>
                    </MessageBubble>
                  </Fade>
                );
              } else if (msg.role === 'task_created') {
                return (
                  <Fade in key={idx} timeout={300}>
                    <TaskCreatedChip
                      icon={<CheckCircleIcon />}
                      label={`✓ Task created: ${msg.task_name} (ID: ${msg.task_id})`}
                      size="small"
                    />
                  </Fade>
                );
              } else if (msg.role === 'error') {
                return (
                  <Fade in key={idx} timeout={300}>
                    <MessageBubble 
                      isUser={false} 
                      elevation={0} 
                      sx={{ 
                        bgcolor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.error.main, 0.2)
                          : theme.palette.error.light,
                        borderColor: theme.palette.error.main,
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Typography variant="body2" sx={{ 
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.error.light
                            : theme.palette.error.dark,
                          lineHeight: 1.6,
                          flex: 1,
                        }}>
                          ⚠️ {msg.content}
                        </Typography>
                        {msg.originalMessage && (
                          <Tooltip title="Retry">
                            <IconButton
                              size="small"
                              onClick={() => retryMessage(msg.originalMessage, idx)}
                              disabled={isSending}
                              sx={{
                                color: theme.palette.mode === 'dark'
                                  ? theme.palette.error.light
                                  : theme.palette.error.dark,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.error.main, 0.15),
                                },
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </MessageBubble>
                  </Fade>
                );
              }
              return null;
            })}

            {currentResponse && (
              <Fade in timeout={300}>
                <MessageBubble isUser={false} elevation={0}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <BotIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Assistant
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                    {currentResponse}
                  </Typography>
                </MessageBubble>
              </Fade>
            )}

            {thinking && (
              <Fade in timeout={200}>
                <ThinkingIndicator>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {thinking}
                  </Typography>
                </ThinkingIndicator>
              </Fade>
            )}

            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder={
                isRecording ? 'Listening… tap stop when done'
                  : isTranscribing ? 'Transcribing…'
                  : 'Type or dictate your message...'
              }
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                if (!e.target.value.trim()) draftDictatedRef.current = false;
              }}
              onKeyPress={handleKeyPress}
              disabled={isSending || isRecording || isTranscribing}
              error={Boolean(dictationError)}
              helperText={dictationError || ''}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.default, 0.6)
                    : theme.palette.background.paper,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
            <Tooltip title={isRecording ? 'Stop and transcribe' : isTranscribing ? 'Transcribing...' : 'Dictate'}>
              <span>
                <IconButton
                  onClick={toggleRecording}
                  disabled={isSending || isTranscribing}
                  color={isRecording ? 'error' : 'primary'}
                  aria-label={isRecording ? 'Stop dictation' : 'Start dictation'}
                  sx={{
                    width: 48,
                    height: 48,
                    border: `1px solid ${isRecording ? theme.palette.error.main : theme.palette.divider}`,
                    ...(isRecording && {
                      bgcolor: alpha(theme.palette.error.main, 0.12),
                      animation: 'dictation-pulse 1.4s ease-in-out infinite',
                      '@keyframes dictation-pulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.4)}` },
                        '50%': { boxShadow: `0 0 0 6px ${alpha(theme.palette.error.main, 0)}` },
                      },
                    }),
                  }}
                >
                  {isTranscribing ? (
                    <CircularProgress size={22} />
                  ) : isRecording ? (
                    <StopIcon />
                  ) : (
                    <MicIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isSending ? "Sending..." : "Send message"}>
              <span>
                <IconButton
                  color="primary"
                  onClick={() => sendMessage()}
                  disabled={!inputMessage.trim() || isSending || isRecording || isTranscribing}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    boxShadow: theme.shadows[3],
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'scale(1.05)',
                      boxShadow: theme.shadows[6],
                    },
                    '&:disabled': {
                      bgcolor: theme.palette.action.disabledBackground,
                      color: theme.palette.action.disabled,
                    },
                  }}
                >
                  {isSending ? (
                    <CircularProgress size={24} sx={{ color: 'inherit' }} />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </InputContainer>
        </ChatContainer>
      </Popover>
    </>
  );
};

export default ChatBot;
